import type { ParsedQs } from 'qs'
import qs from 'qs'
import type { UiState } from 'instantsearch.js'
import type { ContentLanguage } from 'codegen/types'
import type { ParsedQsValue } from 'utils/query'
import { decodeQueryArray } from 'utils/query'
import { algoliaContentIndex } from 'algolia/algolia-content'

import { buildAlgoliaColorFilter } from '@web/components/algolia/utils/build-algolia-color-filter'

export enum SearchableFilters {
  CATEGORY = 'categoryId',
  COLORS = 'colors',
  ORIENTATION = 'format',
  PEOPLE = 'people',
  OBJECTS = 'objects',
}

export const getSearchState = ({
  url,
  lang,
  authorId,
  isStockPhotos,
  categories = {},
}: {
  url: string
  lang: ContentLanguage
  authorId: string
  isStockPhotos: boolean
  categories?: Record<string, { id: string; name: string; slug: string }>
}) => {
  const queryObject = qs.parse(url)

  return queryToSearchState({ queryObject, lang, authorId, isStockPhotos, categories })
}

interface QueryToSearchState {
  uiState: UiState
  other?: {
    expanded: ParsedQsValue
    color: string | null
  }
}

export const queryToSearchState = ({
  queryObject,
  lang,
  authorId,
  isStockPhotos,
  categories = {},
}: {
  queryObject: ParsedQs
  lang: ContentLanguage
  authorId: string
  isStockPhotos: boolean
  categories?: Record<string, { id: string; name: string; slug: string }>
}): QueryToSearchState => {
  const {
    query = '',
    page,
    sortBy,
    format,
    object,
    color,
    expanded,
    withPeople,
    category,
  } = queryObject

  const colorHex = color ? decodeURIComponent(String(color)) : ''

  const colorFilters = buildAlgoliaColorFilter(colorHex)

  const state: UiState = {
    [algoliaContentIndex]: {
      query: decodeURIComponent(String(query)),
      // oxlint-disable-next-line no-negated-condition
      page: !Number(page) ? 1 : Number(page),
      sortBy: sortBy ? decodeURIComponent(String(sortBy)) : '',
      configure: {
        hitsPerPage: 50,
        filters: `authorId:${authorId}${color ? ` AND ${colorFilters}` : ''}`,
      },
      ...(isStockPhotos &&
        withPeople && {
          menu: {
            'flags.hasPeople': decodeQueryArray(withPeople)[0],
          },
        }),
      refinementList: {
        [`${SearchableFilters.ORIENTATION}`]: decodeQueryArray(format),
        [`imageAnalysis.${lang}.${SearchableFilters.OBJECTS}`]: decodeQueryArray(object),
        ...(isStockPhotos && {
          [`${SearchableFilters.CATEGORY}`]: decodeQueryArray(category).map(categoryName => {
            const categoryObject = Object.values(categories).find(
              ({ name }) => name === categoryName,
            )

            return categoryObject ? categoryObject.id : categoryName
          }),
        }),
      },
    },
  }

  return {
    uiState: state,
    other: {
      expanded: decodeQueryArray(expanded),
      color: colorHex.length > 0 ? colorHex : null,
    },
  }
}

export const searchStateToQuery = ({
  searchState,
  lang,
  categories = {},
}: {
  searchState: QueryToSearchState
  lang: ContentLanguage
  categories?: Record<string, { id: string; name: string; slug: string }>
}) => {
  const { query, page, refinementList, sortBy, menu } = searchState.uiState[algoliaContentIndex]

  const format = refinementList ? refinementList[`${SearchableFilters.ORIENTATION}`] : []
  const object = refinementList
    ? refinementList[`imageAnalysis.${lang}.${SearchableFilters.OBJECTS}`]
    : []
  const category =
    refinementList &&
    refinementList[`${SearchableFilters.CATEGORY}`] &&
    refinementList[`${SearchableFilters.CATEGORY}`].length > 0
      ? refinementList[`${SearchableFilters.CATEGORY}`].map(id => categories[id].name)
      : []
  const withPeople = menu ? menu[`flags.hasPeople`] : []

  const expanded = searchState.other?.expanded
  const color = searchState.other?.color

  const queryObject = {
    expanded: expanded ? decodeQueryArray(expanded) : undefined,
    query: query ? query : undefined,
    sortBy: sortBy ? sortBy : undefined,
    page: page && page !== 1 ? page : undefined,
    format: format ? format.map(encodeURIComponent) : undefined,
    object: object ? object.map(encodeURIComponent) : undefined,
    color: color ? encodeURIComponent(color) : undefined,
    category: category ? category.map(encodeURIComponent) : undefined,
    withPeople: withPeople ? withPeople : undefined,
  }

  return qs.stringify(queryObject, { arrayFormat: 'comma' })
}
